import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Mail, 
  Plus, 
  Search, 
  Send, 
  Edit, 
  Trash2, 
  Copy, 
  BarChart3,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Eye,
  Calendar,
  Users,
  MousePointerClick,
  MailOpen,
  ChevronDown,
  ChevronUp,
  Archive,
  ArchiveRestore
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NewsletterCampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const navigate = useNavigate();
  
  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchCampaigns();
  }, [statusFilter]);

  // Auto-polling when any campaign is sending
  useEffect(() => {
    const hasSending = campaigns.some(c => c.status === 'sending');
    if (!hasSending) return;
    
    const interval = setInterval(() => {
      fetchCampaigns(false);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [campaigns]);

  const fetchCampaigns = async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(
        `${API_URL}/api/newsletter/campaigns?${params.toString()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setCampaigns(response.data.campaigns || []);
    } catch (error) {
      if (showLoading) toast.error('Fehler beim Laden der Kampagnen');
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleDelete = async (campaignId) => {
    if (!confirm('Kampagne wirklich löschen?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/newsletter/campaigns/${campaignId}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Kampagne gelöscht');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleDuplicate = async (campaignId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/newsletter/campaigns/${campaignId}/duplicate`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Kampagne dupliziert');
      navigate(`/admin/newsletter-system/edit/${response.data.campaign.id}`);
    } catch (error) {
      toast.error('Fehler beim Duplizieren');
    }
  };

  const handleArchive = async (campaignId, isArchived) => {
    try {
      await axios.put(
        `${API_URL}/api/newsletter/campaigns/${campaignId}/archive`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(isArchived ? 'Kampagne wiederhergestellt' : 'Kampagne archiviert');
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-500/20 text-gray-400',
      scheduled: 'bg-blue-500/20 text-blue-400',
      sending: 'bg-yellow-500/20 text-yellow-400',
      sent: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400',
      archived: 'bg-muted text-muted-foreground'
    };
    
    const labels = {
      draft: 'Entwurf',
      scheduled: 'Geplant',
      sending: 'Wird gesendet',
      sent: 'Gesendet',
      cancelled: 'Abgebrochen',
      archived: 'Archiviert'
    };
    
    const icons = {
      draft: <Edit size={12} />,
      scheduled: <Clock size={12} />,
      sending: <Loader2 size={12} className="animate-spin" />,
      sent: <CheckCircle2 size={12} />,
      cancelled: <AlertCircle size={12} />,
      archived: <Archive size={12} />
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium ${styles[status] || styles.draft}`}>
        {icons[status]}
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const sortedCampaigns = [...campaigns]
    .filter(c => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return c.name?.toLowerCase().includes(query) || c.subject?.toLowerCase().includes(query);
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'created_at' || sortField === 'sent_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      }
      return aVal < bVal ? 1 : -1;
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Mail className="text-primary" size={28} />
            <div>
              <h1 className="font-['Oswald'] text-2xl font-bold text-foreground uppercase tracking-tight">
                Newsletter Kampagnen
              </h1>
              <p className="text-sm text-muted-foreground">
                Erstellen, verwalten und analysieren Sie Ihre Newsletter
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Link to="/admin/newsletter-system/analytics">
              <Button variant="outline" className="gap-2 rounded-none">
                <BarChart3 size={16} />
                Analytics
              </Button>
            </Link>
            <Link to="/admin/newsletter-system/subscribers">
              <Button variant="outline" className="gap-2 rounded-none">
                <Users size={16} />
                Abonnenten
              </Button>
            </Link>
            <Link to="/admin/newsletter-system/new">
              <Button className="gap-2 rounded-none">
                <Plus size={16} />
                Neue Kampagne
              </Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Kampagne suchen..."
              className="pl-10 rounded-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-card border border-border text-sm"
          >
            <option value="">Alle Status</option>
            <option value="draft">Entwürfe</option>
            <option value="sending">Wird gesendet</option>
            <option value="sent">Gesendet</option>
            <option value="archived">Archiviert</option>
          </select>
        </div>

        {/* Campaign List */}
        <div className="bg-card border border-border">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : sortedCampaigns.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Mail className="mx-auto mb-4 h-12 w-12 opacity-50" />
              <p>Keine Kampagnen gefunden</p>
              <Link to="/admin/newsletter-system/new">
                <Button className="mt-4 gap-2 rounded-none">
                  <Plus size={16} />
                  Erste Kampagne erstellen
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/30">
                  <tr>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('name')}
                    >
                      <div className="flex items-center gap-1">
                        Kampagne
                        <SortIcon field="name" />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Status
                    </th>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        Erstellt
                        <SortIcon field="created_at" />
                      </div>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      <div className="flex items-center justify-center gap-1">
                        <Send size={12} />
                        Gesendet
                      </div>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      <div className="flex items-center justify-center gap-1">
                        <MailOpen size={12} />
                        Öffnungen
                      </div>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      <div className="flex items-center justify-center gap-1">
                        <MousePointerClick size={12} />
                        Klicks
                      </div>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {sortedCampaigns.map((campaign) => {
                    const stats = campaign.stats || {};
                    const openRate = stats.sent > 0 ? Math.round((stats.opened / stats.sent) * 100) : 0;
                    const clickRate = stats.sent > 0 ? Math.round((stats.clicked / stats.sent) * 100) : 0;
                    
                    return (
                      <tr key={campaign.id} className="hover:bg-muted/10">
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium text-foreground">{campaign.name}</p>
                            <p className="text-sm text-muted-foreground truncate max-w-xs">{campaign.subject}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          {campaign.status === 'sending' ? (
                            <div>
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-blue-500/20 text-blue-400" data-testid={`status-sending-${campaign.id}`}>
                                <Loader2 size={12} className="animate-spin" />
                                Wird gesendet
                              </span>
                              {stats.total_recipients > 0 && (
                                <div className="mt-2">
                                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                                    <span>{(stats.progress || 0)}/{stats.total_recipients}</span>
                                    <span>{Math.round(((stats.progress || 0) / stats.total_recipients) * 100)}%</span>
                                  </div>
                                  <div className="w-full h-1.5 bg-muted/30 overflow-hidden">
                                    <div 
                                      className="h-full bg-blue-500 transition-all duration-500"
                                      style={{ width: `${Math.round(((stats.progress || 0) / stats.total_recipients) * 100)}%` }}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <>
                              {getStatusBadge(campaign.status)}
                              {campaign.scheduled_at && campaign.status === 'scheduled' && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  {formatDate(campaign.scheduled_at)}
                                </p>
                              )}
                            </>
                          )}
                        </td>
                        <td className="px-4 py-4 text-sm text-muted-foreground">
                          {formatDate(campaign.created_at)}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="text-sm font-medium">{stats.sent || 0}</span>
                          {(stats.failed > 0) && (
                            <span className="text-xs text-red-400 ml-1">({stats.failed} fehlgeschlagen)</span>
                          )}
                        </td>
                        <td className="px-4 py-4 text-center">
                          <div>
                            <span className="text-sm font-medium">{stats.opened || 0}</span>
                            {stats.sent > 0 && (
                              <span className="text-xs text-muted-foreground ml-1">({openRate}%)</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <div>
                            <span className="text-sm font-medium">{stats.clicked || 0}</span>
                            {stats.sent > 0 && (
                              <span className="text-xs text-muted-foreground ml-1">({clickRate}%)</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex items-center justify-end gap-1">
                            {(campaign.status === 'sent' || campaign.status === 'archived') && (
                              <Link to={`/admin/newsletter-system/report/${campaign.id}`}>
                                <Button variant="ghost" size="sm" className="p-2" title="Bericht anzeigen">
                                  <BarChart3 size={16} />
                                </Button>
                              </Link>
                            )}
                            {campaign.status === 'draft' && (
                              <Link to={`/admin/newsletter-system/edit/${campaign.id}`}>
                                <Button variant="ghost" size="sm" className="p-2" title="Bearbeiten">
                                  <Edit size={16} />
                                </Button>
                              </Link>
                            )}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="p-2" 
                              title="Duplizieren"
                              onClick={() => handleDuplicate(campaign.id)}
                            >
                              <Copy size={16} />
                            </Button>
                            {(campaign.status === 'sent' || campaign.status === 'archived') && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="p-2" 
                                title={campaign.status === 'archived' ? 'Wiederherstellen' : 'Archivieren'}
                                onClick={() => handleArchive(campaign.id, campaign.status === 'archived')}
                                data-testid={`archive-btn-${campaign.id}`}
                              >
                                {campaign.status === 'archived' ? <ArchiveRestore size={16} /> : <Archive size={16} />}
                              </Button>
                            )}
                            {campaign.status === 'draft' && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="p-2 text-red-500 hover:text-red-400" 
                                title="Löschen"
                                onClick={() => handleDelete(campaign.id)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
